/**
 * NotFoundPage.jsx
 * ----------------
 * Authenticated 404 — replaces the previous silent `Navigate to="/"`
 * catch-all.  Renders inside DashboardLayout so the user keeps the
 * sidebar, profile, balance widget, etc. — visually guaranteeing
 * they are still logged in.
 *
 * Two key responsibilities:
 *   1. Confirm the user is NOT logged out (cards + sidebar prove it).
 *   2. Help them recover with role-aware "you probably wanted…"
 *      suggestions instead of a dead-end bounce.
 */
import { Link, useLocation, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  Compass, LayoutDashboard, Radio, BookOpen, Sparkles, Wallet,
  Trophy, Calendar, ShieldCheck, Mail, ArrowLeft, ArrowRight,
  Award, Briefcase, Users, GraduationCap, ScrollText, BarChart3,
  KeyRound,
} from "lucide-react";


// Role → ordered list of best-guess suggestions.  Every `to` value is
// a real React Router path registered in App.js.  Order matters: most
// likely destination first.
const SUGGESTIONS_BY_ROLE = {
  delegate: [
    { to: "/dashboard",             icon: LayoutDashboard, label: "My Overview",       hint: "Your personalised home" },
    { to: "/dashboard/road-to-podium", icon: Trophy,        label: "Road to Podium",    hint: "Resume your learning journey" },
    { to: "/dashboard/lms/live-sessions", icon: Radio,     label: "Live Classroom",    hint: "Upcoming sessions & recordings" },
    { to: "/dashboard/lms",         icon: BookOpen,        label: "Learning Academy",  hint: "Courses, scenarios, sandbox" },
    { to: "/dashboard/ai-prep",     icon: Sparkles,        label: "AI Prep",           hint: "Personalised prep with Diplomexa AI" },
    { to: "/dashboard/diploks/personal/wallet", icon: Wallet, label: "Diploks Wallet", hint: "Balance & top-up" },
  ],
  parent: [
    { to: "/dashboard",                       icon: LayoutDashboard, label: "Family Overview", hint: "All your children at a glance" },
    { to: "/dashboard/diploks/parent/children", icon: Users,         label: "My Children",     hint: "Approvals, progress, wallets" },
    { to: "/dashboard/lms/parent/live",       icon: Radio,           label: "Live Classes",    hint: "Sessions your child is attending" },
    { to: "/dashboard/access-passes",         icon: KeyRound,        label: "Access Passes",   hint: "QR passes & visitor passes" },
    { to: "/dashboard/billing",               icon: Wallet,          label: "Billing",         hint: "Invoices & receipts" },
  ],
  chair: [
    { to: "/dashboard",                          icon: LayoutDashboard, label: "Chair Console",     hint: "Today's agenda" },
    { to: "/dashboard/lms/live-sessions",        icon: Radio,           label: "Live Classroom",    hint: "Host or join a session" },
    { to: "/dashboard/lms/chair",                icon: GraduationCap,   label: "Chair Toolkit",     hint: "Crisis kits, motion library" },
    { to: "/dashboard/events",                   icon: Calendar,        label: "My Events",         hint: "Allocations & schedules" },
    { to: "/dashboard/trainer/grading-queue",    icon: ScrollText,      label: "Grading Queue",     hint: "Pending evaluations" },
  ],
  faculty_advisor: [
    { to: "/dashboard",                  icon: LayoutDashboard, label: "Faculty Overview", hint: "Your delegates' progress" },
    { to: "/dashboard/lms/faculty",      icon: GraduationCap,  label: "Faculty Hub",      hint: "Trainings & resources" },
    { to: "/dashboard/lms/live-sessions",icon: Radio,          label: "Live Classroom",   hint: "Schedule sessions" },
    { to: "/dashboard/faculty/schedule", icon: Calendar,       label: "My Schedule",      hint: "All your commitments" },
    { to: "/dashboard/passport",         icon: ShieldCheck,    label: "Delegate Passport",hint: "Profiles & certificates" },
  ],
  school_admin: [
    { to: "/dashboard",                  icon: LayoutDashboard, label: "School Overview",  hint: "Delegates, events, billing" },
    { to: "/dashboard/school/delegates", icon: Users,          label: "My Delegates",     hint: "Roster & registrations" },
    { to: "/dashboard/school/invoices",  icon: Wallet,         label: "Invoices",         hint: "School billing history" },
    { to: "/dashboard/events",           icon: Calendar,       label: "Events",           hint: "Browse upcoming conferences" },
  ],
  event_manager: [
    { to: "/dashboard",                  icon: LayoutDashboard, label: "Operations Hub",  hint: "Today's ops dashboard" },
    { to: "/dashboard/events",           icon: Calendar,       label: "Events",           hint: "Run-of-show across events" },
    { to: "/dashboard/admin/access-control", icon: ShieldCheck, label: "Access Control",  hint: "QR scan & visitor passes" },
    { to: "/dashboard/lms/live-sessions",icon: Radio,          label: "Live Classroom",   hint: "Schedule & monitor sessions" },
    { to: "/dashboard/analytics",        icon: BarChart3,      label: "Analytics",        hint: "Engagement & revenue" },
  ],
  org_admin: [
    { to: "/dashboard",                       icon: LayoutDashboard, label: "Org Overview",   hint: "Your conference dashboard" },
    { to: "/dashboard/events",                icon: Calendar,       label: "Events",          hint: "Create & manage events" },
    { to: "/dashboard/analytics",             icon: BarChart3,      label: "Analytics",       hint: "Across all your events" },
    { to: "/dashboard/billing",               icon: Wallet,         label: "Billing & Plans", hint: "Subscription & invoices" },
    { to: "/dashboard/diploks/wallet",        icon: Sparkles,       label: "Diploks Wallet",  hint: "Org-wide AI budget" },
    { to: "/dashboard/admin/access-control",  icon: ShieldCheck,    label: "Access Control",  hint: "Visitor passes & scanning" },
  ],
  super_admin: [
    { to: "/dashboard",                  icon: LayoutDashboard, label: "Super Admin Console", hint: "Org health & tenancy" },
    { to: "/dashboard/organizations",    icon: Briefcase,      label: "Organisations",       hint: "All tenants" },
    { to: "/dashboard/admin/goodwill-audit", icon: ShieldCheck, label: "Goodwill Audit",     hint: "Diploks compliance" },
    { to: "/dashboard/admin/diploks",    icon: Sparkles,       label: "Diploks Engine",      hint: "Feature costs & ledgers" },
    { to: "/dashboard/watchdog",         icon: BarChart3,      label: "Watchdog",            hint: "Live system health" },
    { to: "/dashboard/revenue",          icon: Wallet,         label: "Revenue",             hint: "Cross-org revenue view" },
  ],
  volunteer: [
    { to: "/dashboard",                   icon: LayoutDashboard,label: "Volunteer Hub",      hint: "Today's tasks" },
    { to: "/dashboard/access/scanner",    icon: KeyRound,       label: "Pass Scanner",       hint: "Scan delegate QR codes" },
    { to: "/dashboard/volunteer/attendance", icon: Users,       label: "Attendance",         hint: "Mark roll call" },
  ],
  sponsor: [
    { to: "/dashboard/sponsor",           icon: Briefcase,      label: "Sponsor Portal",     hint: "Leads, awards, branding" },
    { to: "/dashboard/billing",           icon: Wallet,         label: "Billing",            hint: "Sponsorship invoices" },
  ],
  judge: [
    { to: "/dashboard/judge",             icon: Award,          label: "Judging Hub",        hint: "Your assignments" },
    { to: "/dashboard/leaderboard",       icon: Trophy,         label: "Leaderboard",        hint: "Top delegates" },
  ],
  partner_institution: [
    { to: "/dashboard/partner",           icon: Briefcase,      label: "Partner Portal",     hint: "Referred delegates" },
    { to: "/dashboard/events",            icon: Calendar,       label: "Events",             hint: "Partner-facing event view" },
  ],
};


// Universal fallbacks shown to every role (or when the user has a
// custom role we haven't mapped).  Limited to safely-existing routes.
const UNIVERSAL_FALLBACK = [
  { to: "/dashboard",                       icon: LayoutDashboard, label: "Dashboard",   hint: "Back to your home" },
  { to: "/dashboard/lms/live-sessions",     icon: Radio,           label: "Live Classroom", hint: "Browse sessions" },
  { to: "/dashboard/events",                icon: Calendar,        label: "Events",      hint: "Your events" },
  { to: "/dashboard/billing",               icon: Wallet,          label: "Billing",     hint: "Invoices & plan" },
  { to: "/contact",                         icon: Mail,            label: "Contact",     hint: "Reach the Diplomexa team" },
];


export default function NotFoundPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const attempted = loc.pathname + loc.search + loc.hash;

  const suggestions = (user && SUGGESTIONS_BY_ROLE[user.role])
                          || UNIVERSAL_FALLBACK;
  const firstName = (user?.name || "").split(" ")[0] || "there";

  return (
    <DashboardLayout>
      <div
        data-testid="not-found-page"
        className="max-w-5xl mx-auto"
      >
        {/* Hero */}
        <section className="relative overflow-hidden rounded-3xl border-2 border-[#D4AF37]/40 bg-gradient-to-br from-[#0A192F] via-[#0A192F] to-[#0F4C81] text-white p-8 sm:p-12">
          <div
            aria-hidden
            className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl"
          />
          <div
            aria-hidden
            className="absolute -left-8 -bottom-12 h-56 w-56 rounded-full bg-[#0F4C81]/40 blur-2xl"
          />
          <div className="relative">
            <p className="text-[10px] uppercase tracking-[0.36em] text-[#D4AF37] font-bold inline-flex items-center gap-2">
              <Compass className="h-3 w-3" /> Lost your way?
            </p>
            <h1
              data-testid="not-found-title"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight"
            >
              We couldn&apos;t find that page,&nbsp;{firstName}.
            </h1>
            <p className="mt-3 text-sm sm:text-base text-slate-200 max-w-2xl">
              The link you followed may be out of date, or the page has
              moved. You&apos;re still signed in &mdash; pick a destination
              below and we&apos;ll take you straight there.
            </p>

            {/* Attempted URL — helps users debug when they typed the URL */}
            {attempted && attempted !== "/" ? (
              <div
                data-testid="not-found-attempted-url"
                className="mt-5 inline-flex max-w-full items-center gap-3 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[11px] font-mono text-slate-300"
              >
                <span className="uppercase tracking-[0.18em] text-[#D4AF37] font-bold text-[10px] shrink-0">
                  You tried
                </span>
                <span className="truncate">{attempted}</span>
              </div>
            ) : null}

            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button
                onClick={() => nav("/dashboard", { replace: true })}
                data-testid="not-found-home-cta"
                className="bg-[#D4AF37] hover:bg-[#b8941d] text-[#0A192F] font-semibold uppercase tracking-[0.16em] text-xs"
              >
                <LayoutDashboard className="h-4 w-4 mr-1.5" />
                Go to dashboard
              </Button>
              <button
                type="button"
                onClick={() => nav(-1)}
                data-testid="not-found-back-button"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-xs uppercase tracking-[0.16em] font-semibold text-slate-200 hover:text-white"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Go back
              </button>
            </div>
          </div>
        </section>

        {/* Smart suggestions */}
        <section className="mt-8" data-testid="not-found-suggestions">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            You probably wanted
          </p>
          <h2 className="mt-1 text-xl font-bold text-[#0A192F]">
            Top spots for your role
          </h2>
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {suggestions.map((s) => {
              const Icon = s.icon || Compass;
              const key = s.label.toLowerCase().replace(/\s+/g, "-");
              return (
                <Link
                  key={s.to}
                  to={s.to}
                  data-testid={`not-found-suggestion-${key}`}
                  className="group rounded-xl border border-slate-200 bg-white p-4 hover:border-[#D4AF37] hover:shadow-md transition-all flex items-start gap-3"
                >
                  <div className="h-10 w-10 rounded-lg bg-[#0A192F] text-[#D4AF37] flex items-center justify-center shrink-0 group-hover:bg-[#D4AF37] group-hover:text-[#0A192F] transition-colors">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#0A192F] flex items-center gap-1">
                      {s.label}
                      <ArrowRight className="h-3 w-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-[#0F4C81]" />
                    </p>
                    {s.hint ? (
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        {s.hint}
                      </p>
                    ) : null}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        {/* Polite "still nothing?" footer */}
        <section className="mt-10 rounded-xl border border-slate-200 bg-slate-50 p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-[#0A192F]">
              Still can&apos;t find what you&apos;re looking for?
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Our team responds within one working day.
            </p>
          </div>
          <Link
            to="/contact"
            data-testid="not-found-contact-link"
            className="text-xs uppercase tracking-[0.18em] font-bold text-[#0F4C81] hover:underline inline-flex items-center gap-1.5"
          >
            <Mail className="h-3.5 w-3.5" /> Contact us
          </Link>
        </section>
      </div>
    </DashboardLayout>
  );
}
